// utils/accountsService.js
import axios from "axios";
import client from "../helpers/directusClient";

export const AccountsService = {
  phoneNumberLookup: async (phoneNumber, provider) => {
    try {
      // Use the actual API call
      const response = await axios.post(
        "http://flowmatic.solutions/api/lookup",
        {
          account: phoneNumber,
          provider: provider,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "6d8d99a0cde85b88ddf2649f0ee78d57",
          },
        }
      );
      console.log("Phone lookup response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error performing phone lookup:", error);
      throw error;
    }
  },
  fetchAccounts: async (companyId, token) => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/accounts?filter[company]=${companyId}&fields=*,users.*.*`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching accounts:", error);
      throw error;
    }
  },

  fetchActiveAccounts: async (companyId, token) => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/accounts?filter[company]=${companyId}&filter[status][_eq]=active&fields=*,users.*.*`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching active accounts:", error);
      throw error;
    }
  },

  createAccount: async (accountData, token) => {
    try {
      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/accounts",
        accountData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error creating account:", error);
      throw error;
    }
  },

  updateAccount: async (accountId, accountData, token) => {
    try {
      const response = await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${accountId}`,
        accountData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating account:", error);
      throw error;
    }
  },

  transferFloat: async (
    fromAccountId,
    toAccountId,
    amount,
    token,
    notes = ""
  ) => {
    try {
      // First, get the current balances of both accounts
      const [sourceAccount, destAccount] = await Promise.all([
        axios.get(
          `https://zedlenders.pickmesms.com/items/accounts/${fromAccountId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/accounts/${toAccountId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
      ]);

      // Calculate new balances
      const sourceBalance = parseFloat(sourceAccount.data.data.balance || 0);
      const destBalance = parseFloat(destAccount.data.data.balance || 0);

      if (sourceBalance < amount) {
        throw new Error("Insufficient balance in source account");
      }

      const newSourceBalance = sourceBalance - parseFloat(amount);
      const newDestBalance = destBalance + parseFloat(amount);

      // Update the source account (decrease balance)
      const sourceUpdate = await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${fromAccountId}`,
        {
          balance: newSourceBalance,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Then, update the destination account (increase balance)
      const destUpdate = await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${toAccountId}`,
        {
          balance: newDestBalance,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const companyId = sourceAccount.data.data.company;

      // Log the transaction once with both account references
      const transactionLog = await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          amount: parseFloat(amount),
          transaction_date: new Date().toISOString().split("T")[0],
          company: companyId,
          from_account: fromAccountId,
          to_account: toAccountId,
          is_loan_transaction: false,
          transaction_status: "Completed",
          transaction_type: "Account Transfer",
          notes:
            notes ||
            `Transfer from ${sourceAccount.data.data.name} to ${destAccount.data.data.name}`,
          payment_method: "internal_transfer",
          reference_number: `TRANSFER-${Date.now()}`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        source: sourceUpdate.data.data,
        destination: destUpdate.data.data,
        transaction: transactionLog.data.data,
      };
    } catch (error) {
      console.error("Error transferring float:", error);
      throw error;
    }
  },
};

export default AccountsService;
